import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isHttpParsingError } from '@/util/http';
import { NavigateFunction, useNavigate } from '@/util/react-router-dom';
import { useMemo, useRef, RefObject } from 'react';

function createQueryClient(navigate: RefObject<NavigateFunction>) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error: unknown) => {
          const errorStatus = (error as { status?: number })?.status;

          if (isHttpParsingError(error)) {
            navigate.current('/system-error');
            return false;
          }

          // Don't retry on 4xx errors
          if (errorStatus && errorStatus >= 400 && errorStatus < 500) {
            return false;
          }

          // Otherwise retry up to 3 times
          return failureCount < 3;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function ApiQueryClientProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClient = useMemo(() => createQueryClient(navigateRef), []);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
